import { useRef, useEffect, useState } from "react";
import { motion, useReducedMotion, useInView } from "framer-motion";
import { useTranslation } from "@/lib/i18n";
import { Clock, CheckCircle, Zap, BookOpen } from "lucide-react";

const icons = [Clock, CheckCircle, Zap, BookOpen];

function MetricCountUp({ value, inView, prefersReduced }: { value: string; inView: boolean; prefersReduced: boolean | null }) {
  const [display, setDisplay] = useState(value);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!inView || hasRun.current || prefersReduced) return;
    // Try to extract leading number
    const match = value.match(/^(\d+)/);
    if (!match) { setDisplay(value); return; }
    const target = parseInt(match[1]);
    const rest = value.slice(match[1].length);
    hasRun.current = true;
    let start = 0;
    const duration = 1200;
    const startTime = performance.now();
    const ease = (t: number) => {
      // cubic bezier approx [0.16, 1, 0.3, 1]
      return 1 - Math.pow(1 - t, 3);
    };
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.round(ease(progress) * target);
      setDisplay(`${current}${rest}`);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, value, prefersReduced]);

  return <span className="tabular-nums">{display}</span>;
}

const CornerBrackets = ({ flash }: { flash: boolean }) => (
  <>
    <div className={`absolute top-0 left-0 w-[14px] h-[2px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute top-0 left-0 w-[2px] h-[14px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute top-0 right-0 w-[14px] h-[2px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute top-0 right-0 w-[2px] h-[14px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute bottom-0 left-0 w-[14px] h-[2px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute bottom-0 left-0 w-[2px] h-[14px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute bottom-0 right-0 w-[14px] h-[2px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
    <div className={`absolute bottom-0 right-0 w-[2px] h-[14px] bg-primary/60 transition-all duration-400 ${flash ? "shadow-[0_0_6px_hsl(var(--primary)/0.8)]" : ""}`} />
  </>
);

const ResultsBanner = () => {
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const r = t.aiTeams.results;
  const gridRef = useRef<HTMLDivElement>(null);
  const inView = useInView(gridRef, { once: true, margin: "-50px" });
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (!inView || prefersReduced) return;
    const timer = setTimeout(() => {
      setFlash(true);
      setTimeout(() => setFlash(false), 400);
    }, 1300);
    return () => clearTimeout(timer);
  }, [inView, prefersReduced]);

  const anim = {
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  return (
    <section className="pt-12 pb-28 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={anim.transition}
          className="mb-12 text-center"
        >
          <p className="text-xs font-mono tracking-[0.2em] text-primary/80 mb-2">
            {r.schematicHeader}
          </p>
          <p className="text-[10px] font-mono tracking-[0.15em] text-muted-foreground max-w-2xl mx-auto">
            {r.schematicSub}
          </p>
        </motion.div>

        <div ref={gridRef} className="relative max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 lg:gap-12">
            {r.metrics.map((metric, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={metric.label}
                  initial={anim.initial}
                  whileInView={anim.whileInView}
                  viewport={anim.viewport}
                  transition={{ delay: prefersReduced ? 0 : i * 0.1, ...anim.transition }}
                  className="relative p-6 bg-[#0d1520]"
                >
                  <CornerBrackets flash={flash} />

                  <div className="flex items-center justify-end mb-8">
                    <Icon size={18} className="text-primary/60" strokeWidth={1.5} />
                  </div>

                  <p className="text-5xl sm:text-6xl lg:text-7xl font-display text-foreground leading-none mb-3">
                    <MetricCountUp value={metric.value} inView={inView} prefersReduced={prefersReduced} />
                  </p>

                  <p className="uppercase tracking-[0.15em] text-[10px] font-mono text-muted-foreground">
                    {metric.label}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* CORE_INTELLIGENCE center badge — hidden when empty */}
          {r.coreIntelligence && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none items-center justify-center"
            >
              <div
                className="w-20 h-20 rounded-full border border-primary/40 bg-[#0d1520] flex items-center justify-center"
                style={!prefersReduced ? { animation: "spin 20s linear infinite" } : undefined}
              >
                <div
                  className="w-14 h-14 rounded-full border border-primary/20 flex items-center justify-center"
                  style={!prefersReduced ? { animation: "spin 20s linear infinite reverse" } : undefined}
                >
                  <span className="text-[8px] font-mono tracking-[0.1em] text-accent text-center leading-tight">
                    {r.coreIntelligence}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ResultsBanner;
